import {
  EnvironmentOutlined,
  SearchOutlined,
  ShopOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  AutoComplete,
  Form,
  Input,
  Select,
  Typography,
  type SelectProps,
} from "antd";
import { debounce } from "lodash";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createLeadSubmission,
  getLeadSubmissionById,
  getPlacesByName,
  updateLeadSubmissionProgress,
} from "services/SauceService";
import { getSelfCampaignQueryParams } from "./queryParams";
import {
  getSelfServeCampaignDraft,
  replaceSelfServeCampaignDraft,
  saveSelfServeCampaignCreateStepDraft,
} from "./storage";
import {
  SelfServeCampaignBusinessType,
  SelfServeCampaignDesignStepDraft,
  SelfServeCampaignCreateStepDraft,
  SelfServeCampaignLocations,
  SelfServeCampaignPlatform,
  SelfServeCampaignPromotionType,
  SelfServeCampaignTone,
  SelfServeCampaignDraft,
} from "./types";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import SelfCampaignPrimaryCta from "./components/SelfCampaignPrimaryCta";
import SelfCampaignProgress from "./components/SelfCampaignProgress";
import "./SelfCampaignCreate.scss";

type SelfCampaignCreateFormValues = SelfServeCampaignCreateStepDraft & {
  storeAddressPlaceId?: string;
};

type AddressOption = {
  value: string;
  label: ReactNode;
  placeId: string;
  displayValue: string;
};

const BUSINESS_TYPE_OPTIONS: SelectProps["options"] = [
  {
    label: "Food & Beverage",
    options: [
      {
        value: "restaurant_food_beverage",
        label: "Restaurant / Food & Beverage",
      },
      { value: "cafe_coffee_shop", label: "Cafe / Coffee Shop" },
      { value: "bar_nightlife", label: "Bar / Nightlife" },
    ],
  },
  {
    label: "Travel & Hospitality",
    options: [
      { value: "hotel_resort", label: "Hotel / Resort" },
      { value: "travel_tourism", label: "Travel / Tourism" },
      { value: "event_venue", label: "Event Venue" },
    ],
  },
  {
    label: "Lifestyle",
    options: [
      { value: "beauty_skincare", label: "Beauty / Skincare" },
      { value: "fitness_wellness", label: "Fitness / Wellness" },
      { value: "fashion_apparel", label: "Fashion / Apparel" },
    ],
  },
  {
    label: "Commerce",
    options: [
      { value: "retail_local_shop", label: "Retail / Local Shop" },
      { value: "consumer_brand", label: "Consumer Brand" },
      { value: "e_commerce", label: "E-commerce" },
    ],
  },
  {
    label: "Experiences",
    options: [
      {
        value: "entertainment_attractions",
        label: "Entertainment / Attractions",
      },
      { value: "arts_culture", label: "Arts / Culture" },
    ],
  },
  {
    label: "Other",
    options: [{ value: "other", label: "Other" }],
  },
];

const LOCATION_OPTIONS: SelectProps["options"] = [
  { value: "1", label: "1 location" },
  { value: "2", label: "2-3 locations" },
  { value: "4", label: "4-10 locations" },
  { value: "10", label: "10+ locations" },
];

const DEFAULT_BUSINESS_TYPE: SelfServeCampaignBusinessType =
  "restaurant_food_beverage";
const DEFAULT_LOCATIONS: SelfServeCampaignLocations = "1";
const DEFAULT_PROMOTION_TYPE: SelfServeCampaignPromotionType = "hybrid";
const DEFAULT_PLATFORM: SelfServeCampaignPlatform = "instagram_tiktok";
const DEFAULT_TONE: SelfServeCampaignTone = "fun";
const SELF_SERVE_PRICING_REDIRECT_URL = "https://www.mustard.love/pricing";
const DEFAULT_DESIGN_BRIEF =
  "Showcase your business highlights and unique experience to attract local creators.";

const normalizeLocationsValue = (
  value?: string,
): SelfServeCampaignLocations => {
  if (value === "2-3") {
    return "2";
  }

  if (value === "4-10") {
    return "4";
  }

  if (value === "10+") {
    return "10";
  }

  if (value === "1" || value === "2" || value === "4" || value === "10") {
    return value;
  }

  return DEFAULT_LOCATIONS;
};

const splitFullName = (
  value?: string,
): { firstName: string; lastName: string } => {
  const normalized = (value ?? "").trim();
  if (!normalized) {
    return { firstName: "", lastName: "" };
  }

  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const getTrimmedString = (value: unknown): string => {
  return typeof value === "string" ? value.trim() : "";
};

const normalizeBusinessType = (
  value: unknown,
): SelfServeCampaignBusinessType => {
  switch (value) {
    case "restaurant_food_beverage":
    case "cafe_coffee_shop":
    case "bar_nightlife":
    case "hotel_resort":
    case "travel_tourism":
    case "event_venue":
    case "beauty_skincare":
    case "fitness_wellness":
    case "fashion_apparel":
    case "retail_local_shop":
    case "consumer_brand":
    case "e_commerce":
    case "entertainment_attractions":
    case "arts_culture":
    case "other":
      return value;
    default:
      return DEFAULT_BUSINESS_TYPE;
  }
};

const normalizeAmountLocationsToValue = (
  value: unknown,
): SelfServeCampaignLocations => {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return DEFAULT_LOCATIONS;
  }

  if (numericValue <= 1) {
    return "1";
  }

  if (numericValue <= 3) {
    return "2";
  }

  if (numericValue <= 10) {
    return "4";
  }

  return "10";
};

const mapLeadSubmissionToCreateValues = (
  leadSubmission: Record<string, unknown>,
): Partial<SelfCampaignCreateFormValues> => {
  const firstName = getTrimmedString(leadSubmission.firstName);
  const lastName = getTrimmedString(leadSubmission.lastName);
  const address = getTrimmedString(leadSubmission.address);
  const addressNumber = getTrimmedString(leadSubmission.addressNumber);

  const nextValues: Partial<SelfCampaignCreateFormValues> = {};

  if (firstName) {
    nextValues.firstName = firstName;
  }

  if (lastName) {
    nextValues.lastName = lastName;
  }

  if (address) {
    nextValues.storeAddress = address;
  }

  if (addressNumber) {
    nextValues.floorSuite = addressNumber;
  }

  if ("businessType" in leadSubmission) {
    nextValues.businessType = normalizeBusinessType(
      leadSubmission.businessType,
    );
  }

  if ("amountLocations" in leadSubmission) {
    nextValues.locations = normalizeAmountLocationsToValue(
      leadSubmission.amountLocations,
    );
  }

  return nextValues;
};

const normalizePromotionType = (
  value: unknown,
): SelfServeCampaignPromotionType => {
  if (value === "hybrid") {
    return "hybrid";
  }

  return "experience_credit";
};

const normalizePlatform = (value: unknown): SelfServeCampaignPlatform => {
  if (value === "instagram_tiktok") {
    return "instagram_tiktok";
  }

  return "instagram";
};

const mapLeadSubmissionToDesignValues = (
  leadSubmission: Record<string, unknown>,
): SelfServeCampaignDesignStepDraft | undefined => {
  const campaignType = getTrimmedString(leadSubmission.campaignType);
  const socialMediaTarget = getTrimmedString(leadSubmission.socialMediaTarget);
  const brief = getTrimmedString(leadSubmission.brief);

  if (!campaignType && !socialMediaTarget && !brief) {
    return undefined;
  }

  return {
    promotionType: campaignType
      ? normalizePromotionType(campaignType)
      : DEFAULT_PROMOTION_TYPE,
    platform: socialMediaTarget
      ? normalizePlatform(socialMediaTarget)
      : DEFAULT_PLATFORM,
    brief: brief || DEFAULT_DESIGN_BRIEF,
    tone: DEFAULT_TONE,
  };
};

const buildHydratedCreateStepDraft = (
  values: Partial<SelfCampaignCreateFormValues>,
): SelfServeCampaignCreateStepDraft => {
  return {
    firstName: values.firstName || "",
    lastName: values.lastName || "",
    storeAddress: values.storeAddress || "",
    floorSuite: values.floorSuite || "",
    businessType: values.businessType || DEFAULT_BUSINESS_TYPE,
    locations: normalizeLocationsValue(values.locations),
  };
};

const SelfCampaignCreate: React.FC = () => {
  const [form] = Form.useForm<SelfCampaignCreateFormValues>();
  const location = useLocation();
  const navigate = useNavigate();
  const [addressOptions, setAddressOptions] = useState<AddressOption[]>([]);
  const [loadingAddress, setLoadingAddress] = useState(false);
  const [loadingLeadSubmission, setLoadingLeadSubmission] = useState(true);
  const [savingStepProgress, setSavingStepProgress] = useState(false);
  const [leadSubmissionError, setLeadSubmissionError] = useState<string | null>(
    null,
  );

  const { email, businessName, name, phone, leadSubmissionId: queryLeadId } =
    useMemo(() => {
      return getSelfCampaignQueryParams(location.search);
    }, [location.search]);
  const firstName = Form.useWatch("firstName", form);
  const lastName = Form.useWatch("lastName", form);
  const storeAddress = Form.useWatch("storeAddress", form);
  const businessType = Form.useWatch("businessType", form);
  const locations = Form.useWatch("locations", form);

  const isValidForContinue = useMemo(() => {
    return Boolean(
      firstName?.trim() &&
      lastName?.trim() &&
      storeAddress?.trim() &&
      businessType &&
      locations,
    );
  }, [businessType, firstName, lastName, locations, storeAddress]);

  const initialValues = useMemo(() => {
    const savedDraft = getSelfServeCampaignDraft();
    const createStep = savedDraft?.createStep;
    const fallbackName = name || businessName;
    const parsedName = splitFullName(fallbackName);

    return {
      firstName: createStep?.firstName || parsedName.firstName,
      lastName: createStep?.lastName || parsedName.lastName,
      storeAddress: createStep?.storeAddress || "",
      storeAddressPlaceId: createStep?.storeAddressPlaceId,
      floorSuite: createStep?.floorSuite || "",
      businessType: createStep?.businessType || DEFAULT_BUSINESS_TYPE,
      locations: normalizeLocationsValue(createStep?.locations),
    } satisfies Partial<SelfCampaignCreateFormValues>;
  }, [businessName, name]);

  const loadPlaces = useMemo(() => {
    return debounce(async (addressInput: string) => {
      if (!addressInput || addressInput.trim().length < 3) {
        setAddressOptions([]);
        return;
      }

      setLoadingAddress(true);
      try {
        const places: any = await getPlacesByName({
          name: addressInput.trim(),
        });

        const options: AddressOption[] = (places || []).map((place: any) => {
          const displayValue = `${place.name} - ${place.formatted_address}`;
          return {
            value: displayValue,
            placeId: place.place_id,
            displayValue,
            label: (
              <div className="self-campaign-create__address-option">
                <span className="self-campaign-create__address-name">
                  {place.name}
                </span>
                <span className="self-campaign-create__address-value">
                  {place.formatted_address}
                </span>
              </div>
            ),
          };
        });
        setAddressOptions(options);
      } catch (_error) {
        setAddressOptions([]);
      } finally {
        setLoadingAddress(false);
      }
    }, 350);
  }, []);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  useEffect(() => {
    return () => {
      loadPlaces.cancel();
    };
  }, [loadPlaces]);

  useEffect(() => {
    let isMounted = true;

    const bootstrapLeadSubmission = async () => {
      const currentDraft = getSelfServeCampaignDraft();
      const fallbackBusinessName =
        businessName ||
        currentDraft?.meta?.businessName ||
        name ||
        "My Business";
      const fallbackEmail = email || currentDraft?.meta?.email || "";
      const fallbackPhone = phone || currentDraft?.meta?.phone || "";
      const resolvedName = name || currentDraft?.meta?.name;

      try {
        setLeadSubmissionError(null);
        setLoadingLeadSubmission(true);

        let leadSubmissionId: string | null | undefined =
          queryLeadId || currentDraft?.meta?.leadSubmissionId;

        if (!leadSubmissionId) {
          if (!fallbackEmail) {
            if (isMounted) {
              setLeadSubmissionError(
                "Missing lead params. Please restart from the site entrypoint.",
              );
              setLoadingLeadSubmission(false);
            }
            return;
          }

          const createdLeadSubmission = await createLeadSubmission({
            businessName: fallbackBusinessName,
            businessEmail: fallbackEmail,
            businessPhone: fallbackPhone,
          });

          leadSubmissionId = createdLeadSubmission?.id ?? null;

          if (!leadSubmissionId) {
            window.location.assign(SELF_SERVE_PRICING_REDIRECT_URL);
            return;
          }
        }

        const leadSubmission = await getLeadSubmissionById(leadSubmissionId);
        const leadCreateValues = mapLeadSubmissionToCreateValues(leadSubmission);
        const leadDesignValues = mapLeadSubmissionToDesignValues(leadSubmission);
        const leadBusinessName = getTrimmedString(leadSubmission.businessName);
        const leadEmail = getTrimmedString(leadSubmission.businessEmail);
        const leadPhone = getTrimmedString(leadSubmission.businessPhone);
        const resolvedBusinessName =
          businessName || leadBusinessName || fallbackBusinessName;
        const resolvedEmail = email || leadEmail || fallbackEmail;
        const resolvedPhone = phone || leadPhone || fallbackPhone;
        const hydratedCreateStep = buildHydratedCreateStepDraft(leadCreateValues);

        const isSameLeadAsDraft =
          currentDraft?.meta?.leadSubmissionId === leadSubmissionId;
        const shouldPreservePlaceId =
          isSameLeadAsDraft &&
          currentDraft?.createStep?.storeAddress === hydratedCreateStep.storeAddress &&
          Boolean(currentDraft?.createStep?.storeAddressPlaceId);

        if (shouldPreservePlaceId) {
          hydratedCreateStep.storeAddressPlaceId =
            currentDraft?.createStep?.storeAddressPlaceId;
        }

        const nextDraft: SelfServeCampaignDraft = {
          meta: {
            ...(resolvedEmail ? { email: resolvedEmail } : {}),
            ...(resolvedBusinessName ? { businessName: resolvedBusinessName } : {}),
            ...(resolvedName ? { name: resolvedName } : {}),
            ...(resolvedPhone ? { phone: resolvedPhone } : {}),
            leadSubmissionId,
            leadSubmission,
            ...(isSameLeadAsDraft && currentDraft?.meta?.collabsCache
              ? { collabsCache: currentDraft.meta.collabsCache }
              : {}),
            ...(currentDraft?.meta?.businessTestimonialsCache
              ? {
                  businessTestimonialsCache:
                    currentDraft.meta.businessTestimonialsCache,
                }
              : {}),
          },
          createStep: hydratedCreateStep,
          ...(leadDesignValues ? { designStep: leadDesignValues } : {}),
          ...(isSameLeadAsDraft && currentDraft?.activateStep
            ? { activateStep: currentDraft.activateStep }
            : {}),
        };

        replaceSelfServeCampaignDraft(nextDraft);

        if (isMounted && !form.isFieldsTouched()) {
          form.setFieldsValue({
            ...hydratedCreateStep,
            storeAddressPlaceId: hydratedCreateStep.storeAddressPlaceId,
          });
        }

        if (isMounted) {
          setLeadSubmissionError(null);
          setLoadingLeadSubmission(false);
        }
      } catch (_error) {
        if (isMounted) {
          setLeadSubmissionError(
            "We could not initialize your campaign flow. Please try again.",
          );
          setLoadingLeadSubmission(false);
        }
      }
    };

    void bootstrapLeadSubmission();

    return () => {
      isMounted = false;
    };
  }, [businessName, email, form, name, phone, queryLeadId]);

  const handleContinue = async () => {
    const values = await form.validateFields();
    const currentDraft = getSelfServeCampaignDraft();
    const leadSubmissionId = currentDraft?.meta?.leadSubmissionId;

    if (!leadSubmissionId) {
      setLeadSubmissionError(
        "Lead submission not found. Please restart from the site entrypoint.",
      );
      return;
    }

    try {
      setSavingStepProgress(true);
      setLeadSubmissionError(null);

      const amountLocations = Number(values.locations);

      await updateLeadSubmissionProgress(leadSubmissionId, {
        step: 1,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        address: values.storeAddress.trim(),
        addressNumber: values.floorSuite?.trim(),
        businessType: values.businessType,
        amountLocations,
      });
    } catch (_error) {
      setLeadSubmissionError(
        "We could not save your progress. Please try again.",
      );
      setSavingStepProgress(false);
      return;
    }

    saveSelfServeCampaignCreateStepDraft(
      {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        storeAddress: values.storeAddress.trim(),
        storeAddressPlaceId: values.storeAddressPlaceId,
        floorSuite: values.floorSuite?.trim(),
        businessType: values.businessType,
        locations: values.locations,
      },
      {
        ...currentDraft?.meta,
        ...(email ? { email } : {}),
        ...(businessName ? { businessName } : {}),
        ...(name ? { name } : {}),
        ...(phone ? { phone } : {}),
      },
    );

    navigate({
      pathname: "/self-campaign/design",
      search: location.search,
    });

    setSavingStepProgress(false);
  };

  return (
    <SelfCampaignPageLayout
      rootClassName="self-campaign-create"
      variant="create"
      footer={
        <SelfCampaignPrimaryCta
          label="Continue"
          mode="mobile-sticky"
          onClick={handleContinue}
          disabled={
            !isValidForContinue ||
            loadingAddress ||
            loadingLeadSubmission ||
            savingStepProgress
          }
        />
      }
    >
      <SelfCampaignProgress
        title="Create your first campaign"
        activeStep={2}
        variant="create"
      />

      <section className="self-campaign-create__card">
        <div className="self-campaign-create__intro">
          <h2>Hi {businessName || "there"} 👋</h2>
          <Typography.Paragraph>
            Let&apos;s learn about your business. We&apos;ll tailor your
            campaign with hyper-local creators and recommendations to drive real
            visits.
          </Typography.Paragraph>
          <div className="self-campaign-create__badge">
            <span>🎯</span>
            <span>Better data = Better matches</span>
          </div>
          {leadSubmissionError ? (
            <Typography.Text
              type="danger"
              className="self-campaign-create__lead-error"
            >
              {leadSubmissionError}
            </Typography.Text>
          ) : null}
        </div>

        <Form
          form={form}
          layout="vertical"
          className="self-campaign-create__form"
          autoComplete="off"
        >
          <section className="self-campaign-create__section">
            <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <UserOutlined />
              </span>
              <h3>Personal Information</h3>
            </div>
            <div className="self-campaign-create__grid self-campaign-create__grid--personal">
              <Form.Item
                label={
                  <>
                    First Name <span className="required">*</span>
                  </>
                }
                name="firstName"
                rules={[{ required: true, message: "First Name is required." }]}
              >
                <Input placeholder="Jane" />
              </Form.Item>

              <Form.Item
                label={
                  <>
                    Last Name <span className="required">*</span>
                  </>
                }
                name="lastName"
                rules={[{ required: true, message: "Last Name is required." }]}
              >
                <Input placeholder="Doe" />
              </Form.Item>
            </div>
          </section>

          <div className="self-campaign-create__divider" />

          <section className="self-campaign-create__section">
            <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <EnvironmentOutlined />
              </span>
              <h3>Business Location</h3>
            </div>
            <div className="self-campaign-create__grid self-campaign-create__grid--location">
              <Form.Item
                label={
                  <>
                    Store Address <span className="required">*</span>
                  </>
                }
                name="storeAddress"
                rules={[
                  { required: true, message: "Store Address is required." },
                ]}
              >
                <AutoComplete
                  options={addressOptions}
                  onSearch={(value) => {
                    form.setFieldValue("storeAddressPlaceId", undefined);
                    loadPlaces(value);
                  }}
                  onSelect={(_value, option) => {
                    const selectedOption = option as AddressOption;
                    form.setFieldValue(
                      "storeAddress",
                      selectedOption.displayValue,
                    );
                    form.setFieldValue(
                      "storeAddressPlaceId",
                      selectedOption.placeId,
                    );
                  }}
                >
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="12415 Ventura Blvd..."
                  />
                </AutoComplete>
              </Form.Item>

              <Form.Item
                label={
                  <>
                    Floor / Suite <span className="optional">(Optional)</span>
                  </>
                }
                name="floorSuite"
              >
                <Input placeholder="Suite 200" />
              </Form.Item>
            </div>
          </section>

          <div className="self-campaign-create__divider" />

          <section className="self-campaign-create__section">
            <div className="self-campaign-create__section-title">
              <span className="self-campaign-create__section-icon">
                <ShopOutlined />
              </span>
              <h3>Business Details</h3>
            </div>
            <div className="self-campaign-create__grid self-campaign-create__grid--details">
              <Form.Item
                label={
                  <>
                    Business Type <span className="required">*</span>
                  </>
                }
                name="businessType"
                rules={[
                  { required: true, message: "Business Type is required." },
                ]}
              >
                <Select options={BUSINESS_TYPE_OPTIONS} />
              </Form.Item>

              <Form.Item
                label={
                  <>
                    Locations <span className="required">*</span>
                  </>
                }
                name="locations"
                rules={[{ required: true, message: "Locations is required." }]}
              >
                <Select options={LOCATION_OPTIONS} />
              </Form.Item>
            </div>
          </section>

          <Form.Item name="storeAddressPlaceId" hidden>
            <Input />
          </Form.Item>

          <SelfCampaignPrimaryCta
            label="Continue"
            mode="inline"
            onClick={handleContinue}
            disabled={
              !isValidForContinue ||
              loadingAddress ||
              loadingLeadSubmission ||
              savingStepProgress
            }
          />
        </Form>
      </section>
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignCreate;
